import { useCallback, useEffect, useState } from 'react';
import { addMonths, format, startOfMonth } from 'date-fns';
import { useSupabase } from '../contexts/SupabaseContext';

export interface PaymentTypeSegment {
  type: string;
  amount: number;
  color: string;
}

type PaymentRow = {
  amount: number | null;
  method: string | null;
  channel: string | null;
  gift_card_id: string | null;
  invoice_id: string | null;
  booking_id: string | null;
  product_sale_id: string | null;
  stripe_payment_intent_id: string | null;
  stripe_checkout_session_id: string | null;
};

/** Fixed display order + color for each payment type shown in the ring. */
const TYPE_META: { type: string; color: string }[] = [
  { type: 'Stripe', color: '#635bff' },
  { type: 'Credit', color: '#3b82f6' },
  { type: 'Cash', color: '#10b981' },
  { type: 'Bank transfer', color: '#f59e0b' },
  { type: 'Gift card', color: '#ec4899' },
  { type: 'Check', color: '#8b5cf6' },
  { type: 'Other', color: '#6b7280' },
];

/**
 * Stored credit (wallet / gift card) being spent on an invoice. Not new cash —
 * the money was already counted when the credit was topped up or purchased.
 * The gift_card_id fallback catches legacy redemption rows that predate the
 * channel column (purchases have a gift_card_id but settle nothing).
 */
export function isStoredCreditRedemption(p: {
  channel?: string | null;
  gift_card_id?: string | null;
  invoice_id?: string | null;
  booking_id?: string | null;
  product_sale_id?: string | null;
}): boolean {
  if (p.channel === 'wallet' || p.channel === 'gift_card') return true;
  return !!p.gift_card_id && !!(p.invoice_id || p.booking_id || p.product_sale_id);
}

function classifyPayment(p: PaymentRow): string {
  if (p.gift_card_id) return 'Gift card';
  if (p.stripe_payment_intent_id || p.stripe_checkout_session_id) return 'Stripe';
  switch (p.method) {
    case 'cash':
      return 'Cash';
    case 'bank_transfer':
      return 'Bank transfer';
    case 'credit_card':
    case 'debit_card':
      return 'Credit';
    case 'check':
      return 'Check';
    default:
      return 'Other';
  }
}

/**
 * Completed payments recorded this month, grouped by payment type. "Stripe" is
 * any Stripe-processed payment; gift-card redemptions are their own bucket; all
 * other (manually recorded) payments split by method — cash, bank transfer,
 * credit, check.
 */
export function usePaymentsByTypeThisMonth(businessId?: string) {
  const supabase = useSupabase();

  const [total, setTotal] = useState(0);
  const [cashCollected, setCashCollected] = useState(0);
  const [creditRedeemed, setCreditRedeemed] = useState(0);
  const [segments, setSegments] = useState<PaymentTypeSegment[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!supabase || !businessId) {
      setTotal(0);
      setCashCollected(0);
      setCreditRedeemed(0);
      setSegments([]);
      setLoading(false);
      return;
    }

    try {
      const now = new Date();
      const monthStart = startOfMonth(now);
      const nextMonthStart = startOfMonth(addMonths(now, 1));

      const { data, error } = await supabase
        .from('payments')
        .select(
          'amount, method, channel, gift_card_id, invoice_id, booking_id, product_sale_id, stripe_payment_intent_id, stripe_checkout_session_id'
        )
        .eq('business_id', businessId)
        .eq('status', 'completed')
        .gte('created_at', format(monthStart, "yyyy-MM-dd'T'00:00:00"))
        .lt('created_at', format(nextMonthStart, "yyyy-MM-dd'T'00:00:00"));

      if (error) throw error;

      const byType = new Map<string, number>();
      let sum = 0;
      let redeemed = 0;
      for (const row of (data ?? []) as PaymentRow[]) {
        const amount = row.amount ?? 0;
        if (amount <= 0) continue;
        sum += amount;
        if (isStoredCreditRedemption(row)) redeemed += amount;
        const type = classifyPayment(row);
        byType.set(type, (byType.get(type) ?? 0) + amount);
      }

      const segs = TYPE_META.map(({ type, color }) => ({
        type,
        color,
        amount: byType.get(type) ?? 0,
      })).filter((s) => s.amount > 0);

      setTotal(sum);
      setCashCollected(sum - redeemed);
      setCreditRedeemed(redeemed);
      setSegments(segs);
    } catch (err) {
      console.error('Error loading payments by type:', err);
      setTotal(0);
      setCashCollected(0);
      setCreditRedeemed(0);
      setSegments([]);
    } finally {
      setLoading(false);
    }
  }, [supabase, businessId]);

  useEffect(() => {
    setLoading(true);
    void load();
    const interval = setInterval(() => void load(), 60_000);
    return () => clearInterval(interval);
  }, [load]);

  return { total, cashCollected, creditRedeemed, segments, loading, refresh: load };
}
